import React, { useState, useEffect, useMemo } from 'react';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface CalculationResults {
  memberNights: number;
  daoNights: number;
  creditsPerNight: number;
  totalCreditsSold: number;
  totalRaised: number;
  actualReserve: number;
  actualReserveRate: number;
  grossDAOPotential: number;
  daoRevenueAt40: number;
  reserveYield: number;
  totalCoverage: number;
  estimatedOps: number;
  surplus: number;
  errorMessage?: string;
}

const PlaceholderTable: React.FC<{ onInfoClick: () => void }> = ({ onInfoClick }) => (
  <div style={{ 
    backgroundColor: '#1f2937', 
    padding: '16px', 
    borderRadius: '8px', 
    border: '1px solid #4b5563',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    marginBottom: '8px',
    position: 'relative'
  }}>
    <button 
      onClick={onInfoClick}
      className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
      aria-label="Show calculator key"
    >
      <InformationCircleIcon className="h-7 w-7" />
    </button>
    <div className="overflow-x-auto text-xs">
      <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
        <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
        <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
      </div>
      <div className="divide-y divide-gray-700 text-gray-300">
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Units:</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">-</span></div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">-</span></div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">-</span></div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">-</span></div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">-</span></div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">-</span></div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">-</span></div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
          <div><span className="text-gray-500">Reserve Fund (-%):</span> <span className="font-semibold">-</span></div>
          <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">-</span></div>
        </div>
      </div>
    </div>
  </div>
);

const LegendModal: React.FC<{ isOpen: boolean; onClose: () => void; }> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gray-800 text-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-8 relative">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-gray-400 hover:text-white"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
          
          <h2 className="text-3xl font-bold mb-6 text-center text-blue-300">Calculator Key</h2>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Property Details</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold text-blue-300">Units:</span> Number of rentable units in the property</p>
                <p><span className="font-semibold text-blue-300">Nights for Members:</span> Total available nights per year for DAO members (200 nights × units)</p>
                <p><span className="font-semibold text-blue-300">Credits Sold:</span> Total credits generated from member nights (nights × credits per night)</p>
                <p><span className="font-semibold text-blue-300">Credit Price:</span> Fixed at $1.00 per credit</p>
                <p><span className="font-semibold text-blue-300">Total Raised:</span> Credits sold × credit price</p>
                <p><span className="font-semibold text-blue-300">Purchase Price:</span> Property acquisition cost</p>
                <p><span className="font-semibold text-blue-300">Legal/Admin:</span> Standard $20,000 for legal and administrative setup</p>
                <p><span className="font-semibold text-blue-300">Reserve Fund:</span> Minimum operating capital (calculated as annual operating costs)</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Annual DAO Income</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold text-blue-300">DAO Nights:</span> Remaining nights available for public rental (365 - 200) × units</p>
                <p><span className="font-semibold text-blue-300">Price per Night:</span> Market rate for public rentals</p>
                <p><span className="font-semibold text-blue-300">Gross Potential:</span> DAO nights × price per night</p>
                <p><span className="font-semibold text-blue-300">40% Occupancy:</span> Conservative estimate of actual rental income</p>
                <p><span className="font-semibold text-blue-300">Reserve Yield (5%):</span> Annual return on reserve fund investment</p>
                <p><span className="font-semibold text-blue-300">Total Coverage:</span> Combined DAO income from rentals and reserve yield</p>
                <p><span className="font-semibold text-blue-300">Estimated Ops Cost:</span> Annual operating expenses (utilities, maintenance, cleaning, etc.)</p>
                <p><span className="font-semibold text-blue-300">Surplus:</span> Total coverage minus estimated operating costs</p>
              </div>
            </div>
            
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-yellow-300 mb-2">How It Works</h3>
              <p className="text-sm">
                The calculator automatically determines the required "credits per night" to make each project financially sustainable. 
                It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, 
                legal/admin fees, and a minimum reserve—while also generating a positive projected surplus.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PropertyCalculator() {
  const [propertyName, setPropertyName] = useState('');
  const [listingLink, setListingLink] = useState('');
  const [price, setPrice] = useState(0);
  const [units, setUnits] = useState(0);
  const [nightlyRate, setNightlyRate] = useState(0);
  const [opsCostPerDay, setOpsCostPerDay] = useState(20);
  const [notes, setNotes] = useState('');

  const [results, setResults] = useState<CalculationResults | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const admin = 20000;
  const memberDays = 200;
  const daoDays = 165;
  const yieldRate = 0.05;

  useEffect(() => {
    if (price > 0 && units > 0 && nightlyRate > 0) {
      let creditsPerNight = Math.ceil((price + admin) / (units * memberDays) / 25) * 25;
      let calculatedResults: CalculationResults | null = null;
      let errorMessage: string | undefined;

      for (let i = 0; i < 100; i++) {
        const memberNights = units * memberDays;
        const daoNights = units * daoDays;
        const totalCreditsSold = memberNights * creditsPerNight;
        const totalRaised = totalCreditsSold;
        
        const estimatedOps = units * opsCostPerDay * 365;
        const minReserve = estimatedOps; // Use annual operating costs as minimum reserve
        
        const startupCosts = price + admin + minReserve;

        if (totalRaised < startupCosts) {
          creditsPerNight += 25;
          continue;
        }

        const actualReserve = totalRaised - price - admin;
        const actualReserveRate = actualReserve / price;
        const reserveYield = actualReserve * yieldRate;
        const grossDAOPotential = nightlyRate * daoNights;
        const daoRevenueAt40 = grossDAOPotential * 0.4;
        const totalCoverage = daoRevenueAt40 + reserveYield;
        const surplus = totalCoverage - estimatedOps;

        if (surplus >= 0) {
          calculatedResults = {
            memberNights,
            daoNights,
            creditsPerNight,
            totalCreditsSold,
            totalRaised,
            actualReserve,
            actualReserveRate,
            grossDAOPotential,
            daoRevenueAt40,
            reserveYield,
            totalCoverage,
            estimatedOps,
            surplus,
          };
          break;
        }
        
        creditsPerNight += 25;
      }

      if (!calculatedResults) {
        errorMessage = "Could not find a viable financial model with the given inputs. Please adjust the property details.";
      }
      
      setResults(calculatedResults ? { ...calculatedResults, errorMessage } : { errorMessage } as CalculationResults);
    } else {
      setResults(null);
    }
  }, [price, units, nightlyRate, opsCostPerDay]);

  const resetCalculator = () => {
    setPropertyName('');
    setListingLink('');
    setPrice(0);
    setUnits(0);
    setNightlyRate(0);
    setOpsCostPerDay(20);
    setNotes('');
    setResults(null);
    setErrors([]);
  };

  const isFormValid = propertyName.trim() !== '' && listingLink.trim() !== '' && price > 0 && units > 0 && nightlyRate > 0;
  const showResults = price > 0 && units > 0 && nightlyRate > 0;

  const handleShare = () => {
    const validationErrors: string[] = [];
    if (propertyName.trim() === '') validationErrors.push('Property Name is required.');
    if (price <= 0) validationErrors.push('Property Price must be a positive number.');
    if (units <= 0) validationErrors.push('Number of Units must be a positive number.');
    if (nightlyRate <= 0) validationErrors.push('Avg Nightly Rate must be a positive number.');

    if (listingLink.trim() === '') {
        validationErrors.push('Listing Link is required.');
    } else {
        try {
            // Basic URL validation, can be improved with a regex for more strictness
            new URL(listingLink);
        } catch (_) {
            validationErrors.push('Listing Link must be a valid URL (e.g., https://example.com).');
        }
    }

    if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
    }

    setErrors([]);
    shareToDiscord();
  }

  const shareToDiscord = () => {
    if (!isFormValid || !results) {
      const missingFields: string[] = [];
      if (propertyName.trim() === '') missingFields.push('Property Name');
      if (price <= 0) missingFields.push('Property Price');
      if (units <= 0) missingFields.push('Number of Units');
      if (nightlyRate <= 0) missingFields.push('Avg Nightly Rate');
      if (listingLink.trim() === '') missingFields.push('Listing Link');
      
      alert(`Please fill in all required fields: ${missingFields.join(', ')}.`);
      return;
    }

    const message = `🏗️ **Property Analysis - ${propertyName}**\n\n` +
      `💰 **Property Details:**\n` +
      `• Price: $${price.toLocaleString()}\n` +
      `• Units: ${units}\n` +
      `• Credits Needed: ${results.totalCreditsSold.toLocaleString()} (${results.creditsPerNight}/night)\n` +
      `• Total Raised: $${results.totalRaised.toLocaleString()}\n` +
      `• Reserve Fund: $${results.actualReserve.toLocaleString()} (${(results.actualReserveRate * 100).toFixed(1)}%)\n\n` +
      `📊 **Annual DAO Income:**\n` +
      `• DAO Nights: ${results.daoNights}\n` +
      `• Gross Potential: $${results.grossDAOPotential.toLocaleString()}\n` +
      `• Total Coverage: $${results.totalCoverage.toLocaleString()}\n` +
      `• Surplus: ~$${results.surplus.toLocaleString()}\n\n` +
      `🔗 Listing: ${listingLink}\n\n` +
      `Calculated with BornFree Property Calculator`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://discord.com/channels/@me?message=${encodedMessage}`, '_blank');
  };

  return (
    <div className="space-y-2">
      {/* Input Fields */}
      <div className="space-y-1" style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <label className="text-gray-400 text-sm" style={{ width: '160px', marginRight: '8px' }}>Property Name:</label>
          <input 
            type="text" 
            className="py-1 px-2 text-sm rounded border border-gray-500 placeholder-gray-400 focus:border-blue-400 focus:outline-none" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white', flex: '1', minWidth: '0' }}
            value={propertyName} 
            onChange={e => setPropertyName(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <label className="text-gray-400 text-sm" style={{ width: '160px', marginRight: '8px' }}>Listing Link:</label>
          <input 
            type="text" 
            className="py-1 px-2 text-sm rounded border border-gray-500 placeholder-gray-400 focus:border-blue-400 focus:outline-none" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white', flex: '1', minWidth: '0' }}
            value={listingLink} 
            onChange={e => setListingLink(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <label className="text-gray-400 text-sm" style={{ width: '160px', marginRight: '8px' }}>Property Price ($):</label>
          <input 
            type="number" 
            className="py-1 px-2 text-sm rounded border border-gray-500 placeholder-gray-400 focus:border-blue-400 focus:outline-none" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white', flex: '1', minWidth: '0' }}
            value={price === 0 ? '' : price} 
            onChange={e => setPrice(e.target.value === '' ? 0 : +e.target.value)} 
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <label className="text-gray-400 text-sm" style={{ width: '160px', marginRight: '8px' }}>Number of Units:</label>
          <input 
            type="number" 
            className="py-1 px-2 text-sm rounded border border-gray-500 placeholder-gray-400 focus:border-blue-400 focus:outline-none" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white', flex: '1', minWidth: '0' }}
            value={units === 0 ? '' : units} 
            onChange={e => setUnits(e.target.value === '' ? 0 : +e.target.value)} 
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <label className="text-gray-400 text-sm" style={{ width: '160px', marginRight: '8px' }}>Avg Nightly Rate ($):</label>
          <input 
            type="number" 
            className="py-1 px-2 text-sm rounded border border-gray-500 placeholder-gray-400 focus:border-blue-400 focus:outline-none" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white', flex: '1', minWidth: '0' }}
            value={nightlyRate === 0 ? '' : nightlyRate} 
            onChange={e => setNightlyRate(e.target.value === '' ? 0 : +e.target.value)} 
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <label className="text-gray-400 text-sm relative group" style={{ width: '160px', marginRight: '8px' }}>
            Ops Cost/Day/Unit:
            <InformationCircleIcon className="h-4 w-4 inline-block ml-1 text-gray-500 align-middle" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 w-48 p-2 bg-gray-700 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              cost per day per room; varies by region
            </span>
          </label>
          <div className="flex items-center" style={{ flex: '1' }}>
            <input 
              type="number" 
              className="py-1 px-2 text-sm rounded-l border border-gray-500 placeholder-gray-400 focus:border-blue-400 focus:outline-none" 
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white', width: 'calc(100% - 32px)' }}
              value={opsCostPerDay === 0 ? '' : opsCostPerDay} 
              onChange={e => setOpsCostPerDay(e.target.value === '' ? 0 : +e.target.value)} 
            />
            <div className="flex flex-col">
              <button 
                onClick={() => setOpsCostPerDay(opsCostPerDay + 1)}
                className="px-2 py-0 border border-l-0 border-gray-500 rounded-tr text-white text-xs"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', lineHeight: '1', height: '14px', boxSizing: 'border-box' }}
              >
                ▲
              </button>
              <button 
                onClick={() => setOpsCostPerDay(Math.max(0, opsCostPerDay - 1))}
                className="px-2 py-0 border border-l-0 border-t-0 border-gray-500 rounded-br text-white text-xs"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', lineHeight: '1', height: '14px', boxSizing: 'border-box' }}
              >
                ▼
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <label className="text-gray-400 text-sm" style={{ width: '160px', marginRight: '8px', paddingTop: '4px' }}>Notes:</label>
          <textarea 
            className="py-1 px-2 text-sm rounded border border-gray-500 placeholder-gray-400 focus:border-blue-400 focus:outline-none resize-vertical" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'white', flex: '1', minWidth: '0', minHeight: '60px' }}
            placeholder="Optional notes about the property..."
            value={notes} 
            onChange={e => setNotes(e.target.value)}
          />
        </div>
      </div>

      {/* Results Table */}
      <div className="mt-4">
        {showResults && results ? (
          results.errorMessage ? (
            <p className="text-red-400 text-center">{results.errorMessage}</p>
          ) : (
            <div style={{ 
              backgroundColor: '#1f2937', 
              padding: '16px', 
              borderRadius: '8px', 
              border: '1px solid #4b5563',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              marginBottom: '8px',
              position: 'relative'
            }}>
               <button 
                onClick={() => setIsModalOpen(true)}
                className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                aria-label="Show calculator key"
              >
                <InformationCircleIcon className="h-7 w-7" />
              </button>
              <div className="mb-2 text-center border-b border-gray-700 pb-2">
                <h3 className="text-lg font-bold text-white">{propertyName}</h3>
                <a href={listingLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline break-all">{listingLink}</a>
              </div>
              <div className="overflow-x-auto text-xs">
                <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                  <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                  <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                </div>
                <div className="divide-y divide-gray-700 text-gray-300">
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Units:</span> <span className="font-semibold">{units}</span></div>
                    <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">{daoDays} × {units} = {results.daoNights}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">{memberDays} × {units} = {results.memberNights}</span></div>
                    <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">${nightlyRate.toLocaleString()}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">{results.totalCreditsSold.toLocaleString()} ({results.creditsPerNight}/night)</span></div>
                    <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">${results.grossDAOPotential.toLocaleString()}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                    <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">${results.daoRevenueAt40.toLocaleString()}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">${results.totalRaised.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">${results.reserveYield.toLocaleString()}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">${price.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">${results.totalCoverage.toLocaleString()}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">${admin.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">${results.estimatedOps.toLocaleString()}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                    <div><span className="text-gray-500">Reserve Fund ({ (results.actualReserveRate * 100).toFixed(1) }%):</span> <span className="font-semibold">${results.actualReserve.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Surplus:</span> <span className={`font-bold ${results.surplus >= 0 ? 'text-green-400' : 'text-red-400'}`}>~${results.surplus.toLocaleString()}</span></div>
                  </div>
                  {notes && (
                    <div className="py-2 px-2 border-t border-gray-600 mt-2">
                      <div><span className="text-gray-500">Notes:</span></div>
                      <div className="text-gray-300 text-sm mt-1 whitespace-pre-wrap">{notes}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ) : (
          <PlaceholderTable onInfoClick={() => setIsModalOpen(true)} />
        )}
      </div>
      
      <LegendModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Action Buttons */}
      <div className="flex space-x-2 pt-2">
        <button 
          onClick={handleShare}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors text-sm w-full"
          disabled={!isFormValid}
        >
          Share to Discord
        </button>
        <button
          onClick={resetCalculator}
          className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded transition-colors text-sm w-full"
        >
          Reset Calculator
        </button>
      </div>

      {errors.length > 0 && (
          <div className="mt-2 text-red-400 text-sm">
              <ul className="list-disc pl-5">
                  {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                  ))}
              </ul>
          </div>
      )}
    </div>
  );
} 